export {};

// Types for Storefront GraphQL API responses used by complete-look
interface CLImageNode {
  url: string;
  altText: string | null;
  id: string;
}

interface CLPriceRange {
  maxVariantPrice: { amount: string; currencyCode: string };
  minVariantPrice: { amount: string; currencyCode: string };
}

interface CLVariantNode {
  id: string;
  metafield: { value: string } | null;
}

interface CLProduct {
  id: string;
  handle: string;
  title: string;
  productType: string;
  createdAt: string;
  availableForSale: boolean;
  tags: string[];
  metafield: { value: string } | null;
  featuredImage: CLImageNode | null;
  priceRange: CLPriceRange;
  variants?: {
    edges: { node: CLVariantNode }[];
  };
  variantBySelectedOptions?: { id: string } | null;
}

interface CLProductEdge {
  node: CLProduct;
}

interface CLProductsConnection {
  edges: CLProductEdge[];
}

interface CLGraphQLResponse {
  data?: {
    products?: CLProductsConnection;
  };
}

// Configuration
const clState = {
  currentMarket: (document.querySelector('body[data-country]') as HTMLElement | null)?.dataset.country?.toUpperCase() === 'CA' ? 'CA' : 'US',
  currentLanguage: document.documentElement.lang.toUpperCase(),
  shopUrl: "https://kaseme-dev-page.myshopify.com",
  accessToken: "a815422836370e8d05bd0c6a813dbaa5"
};

// Utilitaires
class CookieManager {
  static setCookie(name: string, value: string, days = 14): void {
    const maxAge = days * 24 * 60 * 60;
    document.cookie = `${name}=${value}; path=/; max-age=${maxAge};`;
  }

  static getCookie(name: string): string | undefined {
    return document.cookie
      .split('; ')
      .find(row => row.startsWith(name + '='))
      ?.split('=')[1];
  }
}

// Fonctions de construction des requêtes GraphQL
function buildQuery(first = 4, queryFilter: string): string {
 const query = `
    query FilteredProducts @inContext(country: ${clState.currentMarket}, language: ${clState.currentLanguage}) {
      products(first: ${first} query:"${queryFilter}", sortKey: BEST_SELLING) {
        edges {
          node {
            id
            handle
            title
            productType
            createdAt
            availableForSale
            tags
            metafield(namespace: "custom_fields", key: "related_products") {
              value
            }
            featuredImage {
              url
              altText
              id
            }
            priceRange {
              maxVariantPrice {
                amount
                currencyCode
              }
              minVariantPrice {
                amount
                currencyCode
              }
            }
          }
        }
      }
    }
  `;
  return query;
}

function buildEnsembleQuery(first = 4, queryFilter: string, productId: string): string {
  return `
     query FilteredProducts @inContext(country: ${clState.currentMarket},language: ${clState.currentLanguage}) {
       products(first: ${first} query:"(tags:ensemble:${queryFilter} AND NOT id:${productId})", sortKey: BEST_SELLING) {
         edges {
           node {
             id
             handle
             title
             productType
             createdAt
             tags
             featuredImage {
               url
               altText
               id
             }
             priceRange {
               maxVariantPrice {
                 amount
                 currencyCode
               }
               minVariantPrice {
                 amount
                 currencyCode
               }
             }
           }
         }
       }
     }
   `;
}

function buildComplementaryQuery(first = 4, queryFilter: string): string {
  const query =  `
     query FilteredProducts @inContext(country: ${clState.currentMarket},language: ${clState.currentLanguage}) {
       products(first: ${first} query:${queryFilter}, sortKey: BEST_SELLING) {
         edges {
           node {
             id
             handle
             title
             productType
             availableForSale
             createdAt
             tags
             featuredImage {
               url
               altText
               id
             }
            variants(first:50) {
              edges {
                node {
                  id
                  metafield(namespace: "custom", key: "phone_model") {
                    value
                  }
                }
              }
            }
             priceRange {
               maxVariantPrice {
                 amount
                 currencyCode
               }
               minVariantPrice {
                 amount
                 currencyCode
               }
             }
           }
         }
       }
     }
   `;
   return query;
}

function  buildImage(currentProduct: CLProduct): string {
  const imageurl = currentProduct.featuredImage?.url ?? '';
  const imagealt = currentProduct.featuredImage?.altText ?? '';
  let imageSection = '';
  if (imageurl == '') {
    imageSection = `<div class="card__media">
        <div class="media media--transparent media--hover-effect --missing-image">
          <img src="${window.themeConfig?.assets?.loadingSpinner ?? ''}" alt="loading" width="75" height="75">
        </div>
    </div>`;
  } else {
    imageSection = `<div class="card__media">
        <div class="media media--transparent media--hover-effect">
          <img
            src="${imageurl}&width=220"
            srcset="${imageurl}&width=130 130w, ${imageurl}&width=180 180w, ${imageurl}&width=220 220w, ${imageurl}&width=350 350w, ${imageurl}&width=440 440w"
            sizes="(min-width: 990px) 220px, (min-width: 500px) 180px, 130px"
            alt="${imagealt}"
            loading="lazy">
        </div>
    </div>`;
  }
  return imageSection;
}

// Fonction pour construire le HTML d'un produit
function buildProductHtml(product: CLProduct): string {
  const { id, title, handle, availableForSale, variantBySelectedOptions, tags, productType, priceRange, metafield, variants } = product;

  let variantId = variantBySelectedOptions ? variantBySelectedOptions.id.replace("gid://shopify/ProductVariant/", "") : null;
  if(productType == "Screen Protector" || productType == "Camera Lens") {
    const phoneModelEl = document.querySelector('[data-phone-model]') as HTMLElement | null;
    const currentPhoneModel = phoneModelEl?.dataset.phoneModel;
    if(currentPhoneModel != null && currentPhoneModel != "") {
      variants?.edges.find(edge => {
        let phoneModels = edge.node.metafield ? edge.node.metafield.value : '';
        if(phoneModels) {
          phoneModels = phoneModels.replaceAll('"', '');
          phoneModels = phoneModels.replaceAll('[', '');
          phoneModels = phoneModels.replaceAll(']', '');
          phoneModels.split(',').forEach(phoneModel => {
            if(phoneModel == currentPhoneModel) {
              variantId = edge.node.id.replace("gid://shopify/ProductVariant/","");
              return true;
            }
          });
        }
        return false;
      });
    }

  }

  let productUrl = handle;
  if(variantId) {
      productUrl = productUrl + '?variant=' + variantId;
  }

  let new_title = title;

  if (new_title.indexOf("Clear Case") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf("Clear Case"));
  }
  if (new_title.indexOf("AirPods Case") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf("AirPods Case"));
  }
  if (new_title.indexOf(" iPad Folio") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf(" iPad Folio"));
  }
  if (new_title.indexOf("MacBook skin") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf("MacBook skin"));
  }
  if (new_title.indexOf(" Folio Case") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf(" Folio Case"));
  }

  let preprice = clState.currentLanguage == 'FR' ? 'À partir de' : 'From';
  if(priceRange.maxVariantPrice.amount == priceRange.minVariantPrice.amount) {
    preprice = '';
  } else if (productType.indexOf('iPad') > -1) {
    preprice = '';
  }
  const urlPrefix = clState.currentLanguage == 'FR' ? '/fr' : '';

    let oosBadge = '';
    let lowStockBadge = '';
    let newBadge = '';
    let colorLabel = '';
    let finalSaleBadge = '';
    const newTag = tags.filter(tag => {
      return tag == 'New';
    });
    const venteFinalTag = tags.filter(tag => {
      return tag == 'final_sale';
    });

    let forceBadge = '';

    if(document.body.dataset.forceBadge == 'true' && document.body.dataset.forceBadgeType?.includes(productType)) {
      forceBadge = `<span class="badge badge--${document.body.dataset.forceBadgeColor}">${document.body.dataset.forceBadgeText}</span>`;
    }

    if(!availableForSale) {
      oosBadge = clState.currentLanguage == 'FR' ? '<span class="badge">Épuisé</span>':'<span class="badge">Out of Stock</span>';
    } else {
      const lowTag = tags.filter(tag => {
        return tag == 'low-stock';
      });
      if(lowTag.length > 0) {
        lowStockBadge = clState.currentLanguage == 'FR' ? '<span class="badge">Bientôt épuisé</span>':'<span class="badge">Low Stock</span>';
      }
    }
    if(newTag.length > 0) {
      newBadge = clState.currentLanguage == 'FR' ? '<span class="badge">Nouveau</span>':'<span class="badge">New</span>';
    }

    if(venteFinalTag.length > 0) {
      finalSaleBadge = clState.currentLanguage == 'FR' ? '<span class="badge">Vente finale</span>':'<span class="badge">Final Sale</span>';
    }

  const quickAddLabel = clState.currentLanguage == 'FR' ? 'Ajout rapide':'Quick Add';
  if(metafield && metafield.value) {
    if (clState.currentLanguage == 'FR') {
      colorLabel = `<div class="card__label">Plus de couleurs disponibles</div>`;
    } else {
      colorLabel = `<div class="card__label">More colors available</div>`;
    }
  }

  const productID = id.replace("gid://shopify/Product/", "");
  const quickAdd = `<div class="quick-add no-js-hidden">
      <modal-opener data-modal="#QuickAdd-${productID}">
        <button id="quick-add-template--${productID}-submit"
          type="submit" name="add"
          class="quick-add__submit button button--full-width button--outline" aria-haspopup="dialog"
          aria-labelledby="quick-add-template--${productID} title-template--${productID}"
          data-product-url="${urlPrefix}/products/${productUrl}">
          ${quickAddLabel}
          <div class="loading__spinner hidden">
            <img src="${window.themeConfig?.assets?.loadingSpinner ?? ''}" alt="loading" width="75" height="75">
          </div>
        </button>
      </modal-opener>
      <quick-add-modal id="QuickAdd-${productID}" class="quick-add-modal">
        <div
          role="dialog"
          aria-label="${new_title}"
          aria-modal="true"
          class="quick-add-modal__content global-settings-popup"
          tabindex="-1"
        >
          <button
            id="ModalClose-${productID}"
            type="button"
            class="quick-add-modal__toggle button button--outline"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" fill="none" viewBox="0 0 18 17">
              <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor">
            </svg>
          </button>
          <div id="QuickAddInfo-${productID}" class="quick-add-modal__content-info"></div>
        </div>
      </quick-add-modal>
    </div>`;

  const enPriceTag = clState.currentLanguage == 'EN' ? '$' : '';
  const frPriceTag = clState.currentLanguage == 'FR' ? '$' : '';
  const currencyTag = clState.currentMarket === 'CA' ? ' CAD' : ' USD';

  return `
    <li class="grid__item">
      <div class="card-wrapper product-card-wrapper underline-links-hover">
        <a href="${urlPrefix}/products/${productUrl}">
          <span class="visually-hidden">${new_title}</span>
        </a>
        <div class="card card--standard card--media">
          <div class="card__inner">
            ${buildImage(product)}
          </div>
          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading">
                <a href="${productUrl}" class="full-unstyled-link">
                  ${new_title}
                </a>
              </h3>
              <div class="card-information">
                <div class="price">
                  <div class="price__container">
                    <div class="price__regular">
                      ${preprice} ${enPriceTag}${parseFloat(priceRange.minVariantPrice.amount).toFixed(2)}${frPriceTag}${currencyTag}
                    </div>
                  </div>
                </div>
                ${colorLabel}
              </div>
            </div>
            <!--${quickAdd}-->
          </div>
          <div class="card__badge top left">
            ${newBadge}
            ${oosBadge}
            ${lowStockBadge}
            ${forceBadge}
            ${finalSaleBadge}
          </div>
        </div>
      </div>
    </li>
  `;
}


// Fonction de fetch principale
async function fetchProductByTitle(productTitle: string, productId: string, productType: string): Promise<CLProductsConnection | false> {
  try {
    const impactQuery = productType == "Phone Case" ? "" : `(title:${productTitle}) OR `;
    const airPodsQuery = productType == "Airpod Cases" ? "" : `(title:${productTitle} AirPods Case) OR `;
    const skinQuery = productType == "Skin" ? "" : `(title:${productTitle} MacBook Skin) OR `;
    const clearCaseQuery = productType == "Clear Case" ? "" : `(title:${productTitle} Clear Case) OR `;
    const iPadFolioQuery = productType == "iPad Folio Case" ? "" : `(title:${productTitle} iPad Folio) OR `;
    const iPadClearCaseQuery = productType == "iPad Clear Case" ? "" : `(title:${productTitle} iPad Clear Case) OR `;
    const kindleCaseLabel = clState.currentLanguage == 'FR' ? 'étui pour liseuse' : 'E-Reader Case';
    const KindleCaseQuery = productType == "Kindle Case" ? "" : `(title:${productTitle} ${kindleCaseLabel}) OR `;
    let queryFilter = `${impactQuery}${airPodsQuery}${skinQuery}${clearCaseQuery}${iPadFolioQuery}${iPadClearCaseQuery}${KindleCaseQuery}AND NOT id:${productId}`;
    queryFilter = queryFilter.slice(0, queryFilter.lastIndexOf(' OR')) + queryFilter.slice(queryFilter.lastIndexOf(' OR') + 3);
    const query = buildQuery(10, queryFilter);

    const response = await fetch(clState.shopUrl + '/api/2026-01/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': clState.accessToken
      },
      body: JSON.stringify({
        query: query
      })
    });

    if (!response.ok) throw new Error('Network response was not ok');
    const data: CLGraphQLResponse = await response.json();
    const products = data.data?.products;
    return products || false;
  } catch (error) {
    console.error('Error fetching products:', error);
    return false;
  }
}

// Fonction de fetch ensemble
async function fetchProductEnsemble(missingProducts: number, ensemble: string, productType: string): Promise<CLProductsConnection | false> {
  try {
    let queryFilter = "";
    if(ensemble != '' && productType == "Phone Case") {
      const capitalized = ensemble.charAt(0).toUpperCase() + ensemble.slice(1);
      queryFilter = `"(name:Phone Loop ${capitalized}) OR (name:Crossbody Phone Strap ${capitalized})`;
      if(missingProducts > 2) {
        queryFilter += ` OR (name:Screen Protector) OR (name:Camera lens protector)"`;
      } else {
        queryFilter += `"`;
      }
    } else {
      // FIX: original was missing opening quote
      queryFilter = `"(name:Screen Protector) OR (name:Camera lens protector)"`;
    }
    const query = buildComplementaryQuery(missingProducts, queryFilter);

    const response = await fetch(clState.shopUrl + '/api/2026-01/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': clState.accessToken
      },
      body: JSON.stringify({
        query: query
      })
    });

    if (!response.ok) throw new Error('Network response was not ok');
    const data: CLGraphQLResponse = await response.json();
    const products = data.data?.products;
    return products || false;
  } catch (error) {
    console.error('Error fetching products:', error);
    return false;
  }
}

// Initialisation
document.addEventListener('DOMContentLoaded', async  () => {
  const section = document.querySelector(`[data-complete-look]`) as HTMLElement | null;
  if (!section) {
    console.warn('Section [data-complete-look] not found');
    return;
  }

  const removeSection = () => {
    const parent = section.parentElement;
    if (parent) {
      const parentId = parent.id;
      // Bug found 2026-09-01: when parent has no id, parentId is "" and the selector
      // below becomes `[href="#"]` — matching ANY bare `href="#"` element anywhere on
      // the page (first in document order), not just this section's own tab link. On
      // FR product pages, complete-look's title matching only strips English suffixes
      // ("AirPods Case", "Clear Case", etc.), so French product titles never match and
      // this section removal fires — collaterally deleting whatever unrelated
      // `href="#"` element happened to come first (confirmed live: it was deleting the
      // Klaviyo back-in-stock trigger). Only run the lookup when there's a real id to
      // match against.
      if (parentId) {
        document.querySelector('[href="#' + parentId + '"]')?.remove();
      }
      parent.remove();
    }
  };

  const productTitle = section.dataset.productTitle;
  const productId = section.dataset.productId;
  const productType = section.dataset.productType;
  const productLimit = parseInt(section.dataset.limit || '4') || 4;

  if (!productTitle || !productId || !productType) {
    console.warn('Missing product data attributes on [data-complete-look]');
    removeSection();
    return;
  }

  const renderProducts = (products: CLProductEdge[], ul: HTMLElement) => {
    let productHTML = '';
    products.forEach(({ node: product }) => {
      productHTML += buildProductHtml(product);
    });
    ul.innerHTML = productHTML;
  };

  try {
    let comparatifTitle = productTitle.replace(' AirPods Case', '');
    comparatifTitle = comparatifTitle.replace(' MacBook Skin', '');
    comparatifTitle = comparatifTitle.replace(' Clear Case', '');
    comparatifTitle = comparatifTitle.replace(' iPad Folio', '');
    comparatifTitle = comparatifTitle.replace(' iPad Clear Case', '');
    comparatifTitle = comparatifTitle.replace(' Folio Case', '');
    comparatifTitle = comparatifTitle.replace(' Kindle Case', '');
    comparatifTitle = comparatifTitle.replace('Phone Loop ', '');

    const sameProductDesignList = await fetchProductByTitle(comparatifTitle, productId, productType);
    let productDesignList: CLProductEdge[] = [];
    if(sameProductDesignList && sameProductDesignList.edges.length > 0) {
      productDesignList = sameProductDesignList.edges.filter(edge => {
        if (edge.node.tags?.includes('do_not_recommend')) return false;
        if(edge.node.productType != productType && edge.node.title != productTitle) {
          let sameProductTitle = edge.node.title.replace(' AirPods Case', '');
          sameProductTitle = sameProductTitle.replace(' MacBook Skin', '');
          sameProductTitle = sameProductTitle.replace(' Clear Case', '');
          sameProductTitle = sameProductTitle.replace(' iPad Folio', '');
          sameProductTitle = sameProductTitle.replace(' iPad Clear Case', '');
          sameProductTitle = sameProductTitle.replace(' Folio Case', '');
          sameProductTitle = sameProductTitle.replace(' Kindle Case', '');
          sameProductTitle = sameProductTitle.replace('Phone Loop ', '');
          sameProductTitle = sameProductTitle.replace(comparatifTitle, '');
          sameProductTitle = sameProductTitle.replace(' ', '');

          if(sameProductTitle.length == 0) {
            return true;
          }
        }
        return false;
      });
    }
    if (productDesignList.length < productLimit) {
      const missingProducts = productLimit - productDesignList.length;
      const ensemble = section.dataset.ensemble || '';
      if(productType == "Phone Case" || productType == "Folio Case" || productType == "Clear Case") {
        const ensembleProducts = await fetchProductEnsemble(missingProducts, ensemble, productType);
        if(ensembleProducts && ensembleProducts.edges.length > 0) {
          const filteredEnsemble = ensembleProducts.edges.filter(e => !e.node.tags?.includes('do_not_recommend'));
          productDesignList.push(...filteredEnsemble);
        }
      }
    }

    if(productDesignList.length > 0) {
      productDesignList = productDesignList.slice(0, productLimit);
      const ul = section.querySelector('ul');
      if (!ul) { removeSection(); return; }
      renderProducts(productDesignList, ul);

      // Re-render cards when the selected variant changes (e.g. device model update)
      // so that Screen Protector / Camera Lens links reflect the new [data-phone-model]
      document.addEventListener('variant-changed', () => {
        renderProducts(productDesignList, ul);
      });
    } else {
      removeSection();
    }
  } catch (error) {
    console.error('Complete the look error:', error);
    removeSection();
  }
});
